import { ElementRef, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '../shared/core/auth.service';
import { catchError, EMPTY, tap } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MidaApiService } from '../mida-api.service';
import { NavigationHistoryService } from '../shared/services/navigation-history.service';

@Injectable({
  providedIn: 'root',
})
export class LoginFacade {
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly midaApiService = inject(MidaApiService);
  private readonly navigationHistory = inject(NavigationHistoryService);

  private _snackBar = inject(MatSnackBar);

  public isLoaderShowing = signal<boolean>(false);
  public passwordState = signal<string>('open');

  public isOtpModalOpen = signal<boolean>(false);
  public isOtpVerifying = signal<boolean>(false);
  public isResendingOtp = signal<boolean>(false);
  public tempLoginResponse = signal<any>(null);

  public togglePasswordState(element: ElementRef): void {
    const input = element.nativeElement as HTMLInputElement;
    if (input.type === 'password') {
      input.type = 'text';
      this.passwordState.set('closed');
    } else {
      input.type = 'password';
      this.passwordState.set('open');
    }
  }

  public login(loginData: any) {
    this.isLoaderShowing.set(true);
    this.authService
      .login(loginData)
      .pipe(
        tap((response: any) => {
          if (response.status == 'success') {
            if (response.otp) {
              this.isLoaderShowing.set(false);
              this.tempLoginResponse.set({ email: loginData.email, password: loginData.password, app: loginData.app });
              this.isOtpModalOpen.set(true);
            } else {
              this.completeLogin(response);
            }
          } else {
            this.isLoaderShowing.set(false);
          }
        }),
        catchError((error) => {
          this.isLoaderShowing.set(false);
          this._snackBar.open(error.error.message, 'Dismiss', {
            horizontalPosition: 'right',
            verticalPosition: 'top',
            duration: 3000,
            panelClass: ['error'],
          });
          return EMPTY;
        })
      )
      .subscribe();
  }

  private async completeLogin(response: any) {
    this.authService.storeTokens(response.token, response.refreshToken);
    const currentUser: any = {};
    currentUser.apps = response.apps;
    currentUser.permissions = response.permissions;
    currentUser.data = {
      ...response.data,
      organisation: response.organistion,
      userUuid: response.userUuid,
      roleName: response.roleName,
    };
    currentUser.organisation = response.organisation;
    this.authService.storeCurrentUser(currentUser);
    this._snackBar.open('Login successfully', 'Dismiss', {
      horizontalPosition: 'right',
      verticalPosition: 'top',
      duration: 3000,
      panelClass: ['success'],
    });
    const redirectUrl = localStorage.getItem('redirectUrl');
    this.isLoaderShowing.set(false);
    this.tempLoginResponse.set(null);

    if (redirectUrl && redirectUrl !== '/login' && redirectUrl !== '/m/midaX/midaX-details') {
      localStorage.removeItem('redirectUrl');
      this.navigationHistory.resetAfterAuthentication(redirectUrl);
      await this.router.navigateByUrl(redirectUrl);
    } else if (redirectUrl === '/login') {
      this.navigationHistory.resetAfterAuthentication('/m/dashboard');
      await this.router.navigate(['/m/dashboard']);
    } else if (redirectUrl === '/m/midaX/midaX-details') {
      this.navigationHistory.resetAfterAuthentication('/m/midaX');
      await this.router.navigate(['/m/midaX']);
    } else {
      this.navigationHistory.resetAfterAuthentication('/m/dashboard');
      await this.router.navigate(['/m/dashboard']);
    }

    await this.authService.initializeSession();

    setTimeout(() => {
      this.isOtpModalOpen.set(false);
    }, 2000);
  }

  public verifyOtp(code: string) {
    this.isOtpVerifying.set(true);
    const email = this.tempLoginResponse()?.email;

    if (!email) {
      this.isOtpVerifying.set(false);
      this._snackBar.open('Session expired. Please calculate login again.', 'Dismiss', {
        horizontalPosition: 'right',
        verticalPosition: 'top',
        duration: 3000,
        panelClass: ['error'],
      });
      return;
    }

    this.authService
      .verifyOtp({ email, otp: Number(code) })
      .pipe(
        tap((response: any) => {
          this.isOtpVerifying.set(false);
          if (response.status === 'success') {
            this.completeLogin(response);
          }
        }),
        catchError((error) => {
          this.isOtpVerifying.set(false);
          this._snackBar.open(error.error?.message || 'Invalid verification code', 'Dismiss', {
            horizontalPosition: 'right',
            verticalPosition: 'top',
            duration: 3000,
            panelClass: ['error'],
          });
          return EMPTY;
        })
      )
      .subscribe();
  }

  public resendOtp() {
    this.isResendingOtp.set(true);
    const email = this.tempLoginResponse()?.email;
    const password = this.tempLoginResponse()?.password;
    const app = this.tempLoginResponse()?.app;

    if (!email || !password) {
      this.isResendingOtp.set(false);
      return;
    }

    this.authService
      .login({ email, password, app })
      .pipe(
        tap((response: any) => {
          this.isResendingOtp.set(false);
          if (response.status === 'success') {
            this._snackBar.open(response.message || 'OTP resent successfully', 'Dismiss', {
              horizontalPosition: 'right',
              verticalPosition: 'top',
              duration: 3000,
              panelClass: ['success'],
            });
          }
        }),
        catchError((error) => {
          this.isResendingOtp.set(false);
          this._snackBar.open(error.error?.message || 'Failed to resend OTP', 'Dismiss', {
            horizontalPosition: 'right',
            verticalPosition: 'top',
            duration: 3000,
            panelClass: ['error'],
          });
          return EMPTY;
        })
      )
      .subscribe();
  }
}
